import { Row, Col, Card, Spin, Avatar, Image, Badge, Space, Button, Tabs, Table, Tag, Tooltip } from "antd";
import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import { ArrowLeftIcon, EyeIcon } from "lucide-react";
import SubscriptionPurchaseModal from "../../components/SubscriptionPurchaseModal";
import { PLAN_COLORS } from "./Index";

const { TabPane } = Tabs;
const SUB_STATUS_COLORS = { active: "green", upcoming: "gold", expired: "default", cancelled: "red" };
const TXN_STATUS_COLORS = { created: "gold", paid: "green", failed: "red", refunded: "default" };

function View() {

  const sectionName     =   "User";
  const routeName       =   "user";

  const params                =   useParams();
  const location              =   useLocation();
  const navigate              =   useNavigate();
  const { request }           =   useRequest();
  const [list, setList]       =   useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions]   = useState([]);
  const [loading, setLoading] =   useState(false);
  const [selectedSub, setSelectedSub]     = useState(null);
  const [subModalOpen, setSubModalOpen]   = useState(false);

  const fetchData = (id) => {
    setLoading(true);
    request({
      url: `${apiPath.viewUser}/${id}`,
      method: "GET",
      onSuccess: (response) => {
        setList(response.data.userInfo);
        setSubscriptions(response.data.subscriptions || []);
        setTransactions(response.data.transactions || []);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData(params.id);
  }, []);

  const renderValue = (key, value) => {
    if (["otp_verify_at", "created_at", "updated_at"].includes(key))
      return value ? moment(value).format("DD-MMM-YYYY HH:mm") : "-";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value ?? "-";
  };

  const fmtDate = (v) => (v ? moment(v).format("DD-MMM-YYYY") : "-");
  const fmtAmount = (v) => `₹${v ? Number(v).toLocaleString("en-IN") : 0}`;

  const subscriptionColumns = [
    {
      title: "Plan",
      key: "en_name",
      render: (_, r) => <span className="cap">{r.en_name || "-"}</span>,
    },
    {
      title: "Segment",
      key: "segment",
      render: (_, r) => (r.segment_id?.en_name || r.segment_en_name || "-"),
    },
    {
      title: "Duration",
      key: "selected_duration",
      render: (_, r) => (r.selected_duration ? `${r.selected_duration} Month${r.selected_duration > 1 ? "s" : ""}` : "-"),
    },
    {
      title: "Amount Paid",
      key: "amount_paid",
      render: (_, r) => fmtAmount(r.amount_paid),
    },
    {
      title: "Valid From",
      key: "start_date",
      render: (_, r) => fmtDate(r.start_date),
    },
    {
      title: "Valid Till",
      key: "end_date",
      render: (_, r) => fmtDate(r.end_date),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => <Tag color={SUB_STATUS_COLORS[r.status] || "default"} className="cap">{r.status || "-"}</Tag>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Tooltip title="View purchase details" color="purple">
          <Button onClick={() => { setSelectedSub(r); setSubModalOpen(true); }}>
            <EyeIcon size={15} />
          </Button>
        </Tooltip>
      ),
    },
  ];

  const transactionColumns = [
    {
      title: "Plan",
      key: "plan_name",
      render: (_, r) => <span className="cap">{r.plan_name || "-"}</span>,
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, r) => fmtAmount(r.amount),
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, r) => (r.duration ? `${r.duration} Month${r.duration > 1 ? "s" : ""}` : "-"),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => <Tag color={TXN_STATUS_COLORS[r.status] || "default"} className="cap">{r.status || "-"}</Tag>,
    },
    {
      title: "Order ID",
      key: "razorpay_order_id",
      render: (_, r) => r.razorpay_order_id || "-",
    },
    {
      title: "Date",
      key: "created_at",
      render: (_, r) => (r.created_at ? moment(r.created_at).format("DD-MMM-YYYY HH:mm") : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Tooltip title="View in Transaction Manager" color="purple">
          <Button onClick={() => navigate(`/transaction/view/${r._id}`)}>
            <EyeIcon size={15} />
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <Tabs defaultActiveKey="profile">

        {/* ---- Profile ---- */}
        <TabPane tab="Profile" key="profile">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card
                title={`${sectionName} Profile`}
                variant={false}
                style={{ borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}
              >
                <Row align="middle" gutter={24}>
                  <Col xs={24} sm={6} md={4} className="text-center">
                    {list?.image ? (
                      <Image
                        src={`${apiPath.assetURL}${list.image}`}
                        width={120}
                        height={120}
                        style={{ borderRadius: "50%", objectFit: "cover" }}
                      />
                    ) : (
                      <Avatar size={120} style={{ fontSize: 40, backgroundColor: "#1677ff" }}>
                        {list?.name?.charAt(0)?.toUpperCase() ?? "U"}
                      </Avatar>
                    )}
                  </Col>

                  <Col xs={24} sm={18} md={20}>
                    <div className="d-flex justify-between">
                      <div>
                        <h2 className="font-semibold mb-1">{list?.name?.toUpperCase() ?? "Anonymous User"}</h2>
                        {list?.firm_name && (
                          <p className="text-muted mb-1" style={{ fontWeight: 500 }}>{list.firm_name}</p>
                        )}
                        <p className="text-muted mb-1">{list?.email ?? "Email not available"}</p>
                        <p className="text-muted">
                          {list?.country_code ? `+${list.country_code}-` : ""}{list?.phone ?? "-"}
                        </p>
                      </div>
                      <div>
                        {list?.otp && <Button>{list?.otp ? `OTP: ${list.otp}` : "No OTP"}</Button>}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Basic Info */}
            <Col span={24} md={12}>
              <Card title="Basic Information" variant={false}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <div className="view-inner-cls"><h5>Firm Name:</h5><h6>{renderValue("firm_name", list.firm_name)}</h6></div>
                  <div className="view-inner-cls"><h5>User Type:</h5><h6>{renderValue("user_type", list.user_type)}</h6></div>
                  <div className="view-inner-cls"><h5>Language:</h5>{renderValue("language", list.language)}</div>
                  <div className="view-inner-cls"><h5>Salesman Code:</h5>{renderValue("salesman_code", list.salesman_code)}</div>
                  <div className="view-inner-cls"><h5>Business Nature:</h5>{renderValue("business_nature", list.business_nature)}</div>
                  <div className="view-inner-cls">
                    <h5>Current Segment:</h5>
                    <h6>{list?.current_segment_id?.en_name ? list.current_segment_id.en_name : renderValue("current_segment_id", list.current_segment_id)}</h6>
                  </div>
                  <div className="view-inner-cls">
                    <h5>Segments:</h5>
                    <h6>
                      {list?.segments?.map((item, index) => (
                        <span key={item._id}>{item?.en_name}{index < list?.segments?.length - 1 && ', '}</span>
                      ))}
                    </h6>
                  </div>
                </Space>
              </Card>
            </Col>

            {/* Contact Details */}
            <Col span={24} md={12}>
              <Card title="Contact Details" variant={false}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <div className="view-inner-cls"><h5>Contact Name:</h5><h6>{renderValue("contact_name", list.contact_name)}</h6></div>
                  <div className="view-inner-cls"><h5>Alt. Phone:</h5><h6>{renderValue("alterntive_phone", list.alterntive_phone)}</h6></div>
                  <div className="view-inner-cls"><h5>Landline:</h5><h6>{renderValue("landline_phone", list.landline_phone)}</h6></div>
                </Space>
              </Card>
            </Col>

            {/* Address */}
            <Col span={24} md={12}>
              <Card title="Address Information" variant={false}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <div className="view-inner-cls"><h5>Address 1:</h5><h6>{renderValue("address_line_1", list.address_line_1)}</h6></div>
                  <div className="view-inner-cls"><h5>Address 2:</h5><h6>{renderValue("address_line_2", list.address_line_2)}</h6></div>
                  <div className="view-inner-cls"><h5>Area:</h5><h6>{renderValue("area", list.area)}</h6></div>
                  <div className="view-inner-cls"><h5>City:</h5><h6>{list?.city?.en_name ? list.city.en_name : renderValue("city", list.city)}</h6></div>
                  <div className="view-inner-cls"><h5>State:</h5><h6>{list?.state?.en_name ? list.state.en_name : renderValue("state", list.state)}</h6></div>
                  <div className="view-inner-cls"><h5>Pincode:</h5><h6>{renderValue("pincode", list.pincode)}</h6></div>
                </Space>
              </Card>
            </Col>

            {/* Account Info */}
            <Col span={24} md={12}>
              <Card title="Account Status" variant={false}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <div className="view-inner-cls">
                    <h5>Plan:</h5>
                    <h6><Tag color={PLAN_COLORS[list?.plan_status] || "default"} className="cap">{list?.plan_status || "free"}</Tag></h6>
                  </div>
                  <div className="view-inner-cls"><h5>Profile Completed:</h5><h6>{list?.is_profile_completed ? <Badge status="success" text="Completed" /> : <Badge status="error" text="Not yet" />}</h6></div>
                  <div className="view-inner-cls"><h5>Active:</h5><h6>{list?.is_active ? <Badge status="success" text="Active" /> : <Badge status="error" text="Inactive" />}</h6></div>
                  <div className="view-inner-cls"><h5>OTP Verified:</h5><h6>{list?.is_otp_verify ? <Badge status="success" text="Verified" /> : <Badge status="error" text="Not Verified" />}</h6></div>
                  <div className="view-inner-cls"><h5>OTP Verified At:</h5><h6>{renderValue("otp_verify_at", list.otp_verify_at)}</h6></div>
                  <div className="view-inner-cls"><h5>Added By:</h5><h6>{renderValue("added_by", list.added_by)}</h6></div>
                  <div className="view-inner-cls"><h5>Created At:</h5><h6>{renderValue("created_at", list.created_at)}</h6></div>
                  <div className="view-inner-cls"><h5>Updated At:</h5><h6>{renderValue("updated_at", list.updated_at)}</h6></div>
                </Space>
              </Card>
            </Col>

            {/* Preferences */}
            <Col span={24} md={12}>
              <Card title="User Preferences" variant={false}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <div className="view-inner-cls"><h5>Sound:</h5><h6>{list?.sound ? <Badge status="success" text="Enabled" /> : <Badge status="error" text="Disabled" />}</h6></div>
                  <div className="view-inner-cls"><h5>Vibration:</h5><h6>{list?.vibration ? <Badge status="success" text="Enabled" /> : <Badge status="error" text="Disabled" />}</h6></div>
                  <div className="view-inner-cls"><h5>Notifications:</h5><h6>{list?.notification ? <Badge status="success" text="Enabled" /> : <Badge status="error" text="Disabled" />}</h6></div>
                </Space>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* ---- Subscriptions ---- */}
        <TabPane tab={`Subscriptions (${subscriptions.length})`} key="subscriptions">
          <Card variant={false}>
            <div className="table-responsive">
              <Table
                rowKey="_id"
                columns={subscriptionColumns}
                dataSource={subscriptions}
                pagination={{ pageSize: 10 }}
                className="ant-border-space"
              />
            </div>
          </Card>
        </TabPane>

        {/* ---- Transactions ---- */}
        <TabPane tab={`Transactions (${transactions.length})`} key="transactions">
          <Card variant={false}>
            <div className="table-responsive">
              <Table
                rowKey="_id"
                columns={transactionColumns}
                dataSource={transactions}
                pagination={{ pageSize: 10 }}
                className="ant-border-space"
              />
            </div>
          </Card>
        </TabPane>
      </Tabs>

      <div className="float-right mt-3">
        <Link className="ant-btn ant-btn-secondary" to={`/${routeName}${location.search}`}>
          <ArrowLeftIcon size={15} /> Back
        </Link>
      </div>

      <SubscriptionPurchaseModal
        open={subModalOpen}
        data={selectedSub}
        onClose={() => setSubModalOpen(false)}
      />
    </Spin>
  );
}

export default View;
